// 單字補充的「點了才展開」：把 HTML 裡出現的補充單字包上可點的
// <span class="ce-vocab">，點擊事件由外層 delegate，資料放 data-word。
//
// 只動文字、絕不動標籤：用 DOMParser 把 HTML 建成節點樹、只走訪
// text node——用字串 replace 的話，"class" 或屬性值裡剛好出現單字
// 就會把整份 HTML 弄壞。
//
// 比對規則：不分大小寫、整字比對（word boundary）、長字優先
// （"climate change" 要先於 "climate" 比對，否則永遠只會中短的）。

/** regex 特殊字元跳脫 */
function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** 純文字 → 可安全塞進 HTML 的字串（逐字稿句子走這裡再 highlight） */
export function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * html 裡出現 words 任一字的地方包上 <span class="ce-vocab" data-word="…">。
 * data-word 一律放單字表裡的原字（不是文章裡的大小寫變體），
 * 點擊端拿它就能對回補充內容。
 *
 * @param {string} html
 * @param {string[]} words 單字表的字（原形）
 * @returns {string}
 */
export function highlightHtml(html, words) {
  const list = (words || []).map((w) => String(w || "").trim()).filter((w) => w !== "");
  if (!html || list.length === 0) {
    return html || "";
  }

  // 長字優先；canonical 表把比對到的變體對回單字表的原字
  const sorted = [...list].sort((a, b) => b.length - a.length);
  const canonical = {};
  sorted.forEach((w) => {
    canonical[w.toLowerCase()] = w;
  });
  const pattern = new RegExp(`\\b(${sorted.map(escapeRegExp).join("|")})\\b`, "gi");

  const doc = new DOMParser().parseFromString(
    `<div id="ce-vocab-root">${html}</div>`,
    "text/html"
  );
  const root = doc.getElementById("ce-vocab-root");
  if (!root) {
    return html;
  }

  // 先收集再改——邊走訪邊改節點樹會亂掉
  const textNodes = [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    const parent = node.parentNode;
    const tag = parent && parent.nodeName ? parent.nodeName.toLowerCase() : "";
    if (tag === "script" || tag === "style") {
      continue;
    }
    // 已經包過的不再包（詳解 HTML 可能被前一輪處理過）
    if (parent && parent.classList && parent.classList.contains("ce-vocab")) {
      continue;
    }
    textNodes.push(node);
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue;
    pattern.lastIndex = 0;
    if (!pattern.test(text)) {
      return;
    }
    pattern.lastIndex = 0;

    const fragment = doc.createDocumentFragment();
    let cursor = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > cursor) {
        fragment.appendChild(doc.createTextNode(text.slice(cursor, match.index)));
      }
      const span = doc.createElement("span");
      span.className = "ce-vocab";
      span.setAttribute("data-word", canonical[match[0].toLowerCase()] || match[0]);
      span.textContent = match[0];
      fragment.appendChild(span);
      cursor = match.index + match[0].length;
    }
    if (cursor < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(cursor)));
    }
    node.parentNode.replaceChild(fragment, node);
  });

  return root.innerHTML;
}
